import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Row, Col, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { motion } from 'framer-motion';
import { API_URL } from "./config";

const SoftworksAdminSplash = () => {
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState({});
  const [companyBranchDetails, setCompanyBranchDetails] = useState(null);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [openShiftDetails, setOpenShiftDetails] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCompanyBranchDetails = async () => {
      const companyId = localStorage.getItem('company_id');
      const branchId = localStorage.getItem('branch_id'); 
      const operatorId = localStorage.getItem('user_id');

      if (!operatorId) {
        console.log('Operator ID not found in local storage.');
        setLoading(false);
        return;
      }

      const url = `${API_URL}/companysetup/getFullCompanyBranchDetails/${companyId}/${branchId}`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        const details = data[0];

        // Store company and branch details in local storage
        localStorage.setItem('companyName', details.company_name);
        localStorage.setItem('branchName', details.branch_name);
        localStorage.setItem('branchLocation', details.branch_location);
        localStorage.setItem('email', details.email);
        localStorage.setItem('phone', details.phone);
        localStorage.setItem('registrationNumber', details.registration_number);
        localStorage.setItem('vatNumber', details.vat_number);

        setCompanyBranchDetails(details);
        fetchOpenShift(companyId, branchId, operatorId);
      } catch (error) {
        console.error('Error fetching company branch details:', error);
        setLoading(false); // Set loading to false on error
      }
    };

    const fetchOpenShift = async (companyId, branchId, operatorId) => {
      const shiftStatus = 'open';
      const url = `${API_URL}/shift/getShiftByCompanyBranchOperatorStatus/${companyId}/${branchId}/${operatorId}/${shiftStatus}`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();

        if (data && data.length > 0) {
          setShiftOpen(true);
          setOpenShiftDetails(data[0]);
        } else {
          navigate('/SoftworksAdminOpenShift');
        }
      } catch (error) {
        console.error('Error fetching open shift:', error);
        setLoading(false); // Set loading to false on error
        navigate('/SoftworksAdminOpenShift');
      }
    };

    const checkShiftDetails = () => {
      const userDetailsKeys = [
        'user_id',
        'userName',
        'async_client_profile_id',
        'async_role',
        'async_category',
        'async_username',
        'branch_id',
        'company_id',
      ];

      const retrievedDetails = {};
      userDetailsKeys.forEach(key => {
        retrievedDetails[key] = localStorage.getItem(key);
      });

      setUserDetails(retrievedDetails);
    };

    const logLocalStorage = () => {
      console.log('Local Storage Items:');
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        const value = localStorage.getItem(key);
        console.log(`${key}: ${value}`);
      }
    };

    const timer = setTimeout(() => {
      checkShiftDetails();
      fetchCompanyBranchDetails();
      logLocalStorage(); // Log items in local storage
    }, 2000);

    return () => clearTimeout(timer);
  }, [navigate]);

  useEffect(() => {
    const storedShiftDetails = localStorage.getItem('shiftDetails');
    if (storedShiftDetails) {
      const shiftDetails = JSON.parse(storedShiftDetails);
      setOpenShiftDetails(shiftDetails);
      setShiftOpen(true);

      // Show SweetAlert to display shift details only if on the splash page
      if (window.location.pathname === '/SoftworksAdminSplash') {
        Swal.fire({
          title: 'Open Shift Found!',
          html: `
            <strong>Shift ID:</strong> ${shiftDetails.shift_id}<br>
            <strong>Branch ID:</strong> ${shiftDetails.branch_id}<br>
            <strong>Branch Name:</strong> ${shiftDetails.branch_name}<br>
            <strong>Company ID:</strong> ${shiftDetails.company_id}<br>
            <strong>Operator ID:</strong> ${shiftDetails.operator_id}<br>
            <strong>Date Time Open:</strong> ${shiftDetails.date_time_open}<br>
            <strong>Shift Comment:</strong> ${shiftDetails.shift_comment}<br>
          `,
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'Proceed with Open Shift',
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            navigate('/SoftworksStore', {
              state: { 
                shiftDetails,
                userDetails,
                companyBranchDetails,
              }
            });
          } else {
            navigate('/MainMenu');
          }
        });
      }
    }

    if (shiftOpen && openShiftDetails) {
      // Storing shift details in local storage
      const shiftDetails = {
        shift_id: openShiftDetails.shift_id,
        company_id: openShiftDetails.company_id,
        branch_id: openShiftDetails.branch_id,
        operator_id: openShiftDetails.operator_id,
        date_time_open: openShiftDetails.date_time_open,
        shift_comment: openShiftDetails.shift_comment,
      };

      localStorage.setItem('shiftDetails', JSON.stringify(shiftDetails));
      console.log('Shift Details stored in local storage:', shiftDetails);
    } else if (!shiftOpen) {
      navigate('/SoftworksAdminOpenShift');
    }
  }, [shiftOpen, openShiftDetails, userDetails.userName, navigate]);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const cardVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1 }
  };

  return (
    <div className="min-vh-100 py-5" style={{ 
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
    }}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <header className="text-center mb-5">
            <h1 className="display-4 fw-bold" style={{ color: '#6f42c1' }}>
              Welcome to Softworks Admin
            </h1>
            <p className="text-muted lead">System initialization in progress</p>
          </header>
        </motion.div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner 
              animation="border" 
              variant="purple" 
              style={{ color: '#6f42c1', width: '3rem', height: '3rem' }}
            />
            <p className="mt-3 text-muted">Loading system components...</p>
          </div>
        ) : (
          <motion.div
            variants={{
              initial: { opacity: 0 },
              animate: { opacity: 1, transition: { staggerChildren: 0.1 } }
            }}
            initial="initial"
            animate="animate"
          >
            <Row className="g-4">
              <Col lg={6}>
                <motion.div variants={cardVariants}>
                  <Card className="shadow-lg h-100">
                    <Card.Header className="bg-purple text-white py-3" style={{ backgroundColor: '#6f42c1' }}>
                      <h4 className="mb-0">User Information</h4>
                    </Card.Header>
                    <Card.Body className="p-4">
                      <Row className="g-3">
                        {[
                          { label: 'User ID', value: userDetails.user_id },
                          { label: 'Username', value: userDetails.userName },
                          { label: 'Role', value: userDetails.async_role },
                          { label: 'Category', value: userDetails.async_category },
                          { label: 'Email', value: userDetails.async_username },
                          { label: 'Branch ID', value: userDetails.branch_id },
                          { label: 'Company ID', value: userDetails.company_id }
                        ].map((item, index) => (
                          <Col md={6} key={index}>
                            <div className="detail-box p-3 rounded" style={{ backgroundColor: 'rgba(111, 66, 193, 0.05)' }}>
                              <div className="text-muted mb-1 small">{item.label}</div>
                              <div className="fw-bold">{item.value || 'N/A'}</div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>

              {shiftOpen && companyBranchDetails && (
                <Col lg={6}>
                  <motion.div variants={cardVariants}>
                    <Card className="shadow-lg h-100">
                      <Card.Header className="bg-purple text-white py-3" style={{ backgroundColor: '#6f42c1' }}>
                        <h4 className="mb-0">Company & Branch Details</h4>
                      </Card.Header>
                      <Card.Body className="p-4">
                        <Row className="g-3">
                          {[
                            { label: 'Company Name', value: companyBranchDetails.company_name },
                            { label: 'Branch Name', value: companyBranchDetails.branch_name },
                            { label: 'Location', value: companyBranchDetails.branch_location },
                            { label: 'Email', value: companyBranchDetails.email },
                            { label: 'Phone', value: companyBranchDetails.phone },
                            { label: 'Registration No.', value: companyBranchDetails.registration_number },
                            { label: 'VAT Number', value: companyBranchDetails.vat_number }
                          ].map((item, index) => (
                            <Col md={6} key={index}>
                              <div className="detail-box p-3 rounded" style={{ backgroundColor: 'rgba(111, 66, 193, 0.05)' }}>
                                <div className="text-muted mb-1 small">{item.label}</div>
                                <div className="fw-bold">{item.value || 'N/A'}</div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              )}
            </Row>

            <motion.div 
              className="text-center mt-4"
              variants={fadeInUp}
            >
              <Card className="shadow-sm border-0 bg-white p-3">
                <div className={`d-inline-block px-4 py-2 rounded-pill ${shiftOpen ? 'bg-success' : 'bg-danger'}`}>
                  <span className="text-white">
                    {shiftOpen ? 'Active Shift Available' : 'No Active Shift'}
                  </span>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </Container>

      <style jsx>{`
        .detail-box {
          transition: all 0.3s ease;
        }
        .detail-box:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(111, 66, 193, 0.1);
        }
        .card {
          border-radius: 15px;
          border: none;
          transition: all 0.3s ease;
        }
        .card-header {
          border-radius: 15px 15px 0 0 !important;
        }
        .spinner-border {
          border-width: 0.25em;
        }
        @media (max-width: 768px) {
          .card {
            margin-bottom: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default SoftworksAdminSplash;